import { useState, useMemo } from 'react'
import { Search, ExternalLink, Building2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useCentralGovernmentLinks } from '@/hooks/use-supabase-data'

export function CentralGovernmentLinksSection() {
  const { links, loading, error } = useCentralGovernmentLinks()
  const [searchQuery, setSearchQuery] = useState('')

  // Filter links based on search query
  const filteredLinks = useMemo(() => {
    if (!searchQuery.trim()) return links

    const query = searchQuery.toLowerCase()
    return links.filter(link => 
      link.service_name?.toLowerCase().includes(query) ||
      link.ministry?.toLowerCase().includes(query) ||
      link.category?.toLowerCase().includes(query) ||
      link.url?.toLowerCase().includes(query)
    )
  }, [links, searchQuery])

  // Get unique categories for filtering
  const categories = useMemo(() => {
    const cats = links
      .map(link => link.category)
      .filter((cat): cat is string => cat !== null && cat !== '')
    return Array.from(new Set(cats)).sort()
  }, [links])

  // Group links by category
  const groupedLinks = useMemo(() => {
    const grouped: Record<string, typeof links> = {}
    filteredLinks.forEach(link => {
      const category = link.category || 'Other'
      if (!grouped[category]) {
        grouped[category] = []
      }
      grouped[category].push(link)
    })
    return grouped
  }, [filteredLinks])

  if (loading) {
    return (
      <section className="py-4 sm:py-8">
        <Card>
          <CardHeader className="pb-3 sm:pb-6">
            <Skeleton className="h-6 sm:h-8 w-48 sm:w-64 mb-2 rounded-xl" />
            <Skeleton className="h-3 sm:h-4 w-full sm:w-96 rounded-xl" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-9 sm:h-10 w-full mb-4 sm:mb-6 rounded-xl" />
            <div className="space-y-2 sm:space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 sm:h-20 w-full rounded-xl" />
              ))}
            </div>
          </CardContent>
        </Card>
      </section>
    )
  }

  if (error) {
    return (
      <section className="py-4 sm:py-8">
        <Card>
          <CardContent className="pt-4 sm:pt-6">
            <div className="text-center py-6 sm:py-8">
              <p className="text-destructive text-xs sm:text-sm">{error}</p>
            </div>
          </CardContent>
        </Card>
      </section>
    )
  }

  return (
    <section className="py-4 sm:py-8">
      <Card>
        <CardHeader className="pb-3 sm:pb-6">
          <CardTitle className="flex items-center gap-2 text-lg sm:text-xl md:text-2xl">
            <Building2 className="w-4 h-4 sm:w-5 sm:h-5" />
            Central Government Services
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm mt-1">
            Search and access important central government services and schemes
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search Bar */}
          <div className="mb-4 sm:mb-6">
            <div className="relative">
              <Search className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <Input
                type="text"
                placeholder="Search by service name, ministry, category, or URL..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 sm:pl-10 pr-3 sm:pr-4 py-1.5 sm:py-2 w-full text-sm sm:text-base"
              />
            </div>
            {searchQuery && (
              <p className="text-xs sm:text-sm text-muted-foreground mt-1.5 sm:mt-2">
                Found {filteredLinks.length} result{filteredLinks.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* Links List */}
          {filteredLinks.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <Building2 className="w-10 h-10 sm:w-12 sm:h-12 text-muted-foreground mx-auto mb-3 sm:mb-4" />
              <p className="font-inter text-xs sm:text-sm text-muted-foreground">
                {searchQuery ? 'No services found matching your search.' : 'No central government services available.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              {Object.entries(groupedLinks).map(([category, categoryLinks]) => (
                <div key={category}>
                  <h3 className="font-poppins text-sm sm:text-lg font-semibold text-foreground mb-2 sm:mb-3 flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5">
                      {category}
                    </Badge>
                    <span className="text-xs sm:text-sm text-muted-foreground">
                      ({categoryLinks.length} service{categoryLinks.length !== 1 ? 's' : ''})
                    </span>
                  </h3>
                  <div className="space-y-2 sm:space-y-3">
                    {categoryLinks.map((link, index) => (
                      <div
                        key={index}
                        className="p-3 sm:p-4 rounded-xl bg-card border border-border hover:border-primary/50 hover:bg-primary/5 transition-all duration-200"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-poppins text-sm sm:text-base font-semibold text-foreground mb-1 break-words">
                              {link.service_name || 'Unnamed Service'}
                            </h4>
                            {link.ministry && (
                              <p className="font-inter text-xs sm:text-sm text-muted-foreground mb-1.5 sm:mb-2">
                                {link.ministry}
                              </p>
                            )}
                            {link.url && (
                              <a
                                href={link.url.startsWith('http') ? link.url : `https://${link.url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs sm:text-sm text-primary hover:underline break-all"
                              >
                                <span className="truncate max-w-full sm:max-w-none">{link.url}</span>
                                <ExternalLink className="w-3 h-3 flex-shrink-0" />
                              </a>
                            )}
                          </div>
                          {link.url && (
                            <Button
                              asChild
                              size="sm"
                              className="flex-shrink-0 w-full sm:w-auto text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2"
                            >
                              <a
                                href={link.url.startsWith('http') ? link.url : `https://${link.url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-1 sm:gap-1.5"
                              >
                                Visit
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  )
}
